import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, ArrowRight, ShieldCheck, Mail } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

const STATUS = {
  pass: { icon: CheckCircle2, cls: "text-green-400", label: "Pass" },
  warn: { icon: AlertTriangle, cls: "text-yellow-400", label: "Needs attention" },
  fail: { icon: XCircle, cls: "text-red-400", label: "At risk" },
  unknown: { icon: HelpCircle, cls: "text-fog", label: "Could not verify" },
};

export default function ExposureCheck() {
  const [domain, setDomain] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [lead, setLead] = useState({ name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setRunning(true);
    setResult(null);
    setSent(false);
    try {
      const { data } = await api.post("/exposure-check", { domain: domain.trim() });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const sendSummary = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/exposure-check/email", {
        check_id: result.check_id,
        email: lead.email,
        name: lead.name,
      });
      toast.success(data.message);
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout
      seoTitle="Free Email & Website Exposure Check | Aliwer"
      seoDescription="Free, instant security exposure check using public information only: SPF, DKIM, DMARC email protection and website HTTPS/TLS. No scans, no account, no obligation."
      path="/exposure-check"
      eyebrow="Free tool · public information only"
      title="Check your exposure. Free, in seconds."
      subtitle="Enter your practice or business domain and we'll check your public email-security records (SPF, DKIM, DMARC) and website encryption — the same first checks an attacker runs. No scans of your systems, no account required."
      testid="exposure-check-page"
    >
      <Reveal>
        <form onSubmit={run} className="flex flex-col sm:flex-row gap-3 max-w-2xl mb-4" data-testid="exposure-form">
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="yourpractice.com"
            required
            data-testid="exposure-domain-input"
            className="flex-1 bg-transparent border border-white/15 rounded-full px-6 py-3.5 text-paper text-sm outline-none focus:border-white/50 transition-colors"
          />
          <button
            type="submit"
            disabled={running}
            data-testid="exposure-run-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
          >
            {running ? "Checking…" : "Run the free check"}
            {!running && <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
          </button>
        </form>
        <p className="text-xs text-fog/80 max-w-2xl mb-12">
          We only read public DNS records and your site's HTTPS certificate — nothing touches your internal
          systems. Results aren't shared, and you decide if you want the written summary.
        </p>
      </Reveal>

      {result && (
        <Reveal>
          <div className="mb-12" data-testid="exposure-results">
            <div className="flex items-baseline gap-4 mb-6">
              <h2 className="font-display text-2xl tracking-tight text-paper">{result.domain}</h2>
              <span className="text-sm text-fog" data-testid="exposure-score">
                {result.passed} of {result.total} checks passed
              </span>
            </div>
            <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10">
              {result.checks.map((c) => {
                const s = STATUS[c.status] || STATUS.unknown;
                return (
                  <div key={c.key} className="flex items-start gap-4 bg-black/40 p-5 sm:p-6" data-testid={`exposure-check-${c.key}`}>
                    <s.icon size={19} className={`${s.cls} mt-0.5 shrink-0`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-sm font-medium text-paper">{c.label}</h3>
                        <span className={`text-[10px] uppercase tracking-widest ${s.cls}`}>{s.label}</span>
                      </div>
                      <p className="mt-1.5 text-sm text-fog leading-relaxed max-w-3xl">{c.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Lead capture */}
            {!sent ? (
              <form onSubmit={sendSummary} className="mt-8 rounded-2xl border border-white/15 bg-surface p-7" data-testid="exposure-lead-form">
                <div className="flex items-center gap-2.5 mb-2">
                  <Mail size={16} className="text-fog" />
                  <h3 className="font-display text-lg tracking-tight text-paper">Get the one-page written summary</h3>
                </div>
                <p className="text-sm text-fog mb-5 max-w-xl">
                  We'll email you this result as a plain-English summary you can keep for your records or share
                  with your IT provider. Free, no obligation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="text"
                    value={lead.name}
                    onChange={(e) => setLead((l) => ({ ...l, name: e.target.value }))}
                    placeholder="Your name"
                    data-testid="exposure-lead-name"
                    className="flex-1 bg-black border border-white/15 rounded-full px-5 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
                  />
                  <input
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))}
                    placeholder="you@yourpractice.com"
                    required
                    data-testid="exposure-lead-email"
                    className="flex-1 bg-black border border-white/15 rounded-full px-5 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="exposure-lead-submit"
                    className="rounded-full bg-paper text-black text-sm font-medium px-7 py-3 hover:bg-white transition-colors disabled:opacity-60"
                  >
                    {sending ? "Sending…" : "Email my summary"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="mt-8 rounded-2xl border border-white/15 bg-surface p-7 text-center" data-testid="exposure-lead-sent">
                <CheckCircle2 size={24} className="mx-auto text-green-400" />
                <p className="mt-3 text-sm text-paper">Summary sent — check your inbox.</p>
              </div>
            )}

            {/* Upsell */}
            <div className="mt-8 flex flex-col md:flex-row md:items-center gap-6 rounded-2xl border border-white/10 bg-black/40 p-7" data-testid="exposure-upsell">
              <ShieldCheck size={24} className="text-paper shrink-0" strokeWidth={1.3} />
              <p className="flex-1 text-sm text-fog leading-relaxed">
                This check only covers what's publicly visible. The{" "}
                <span className="text-paper">$499 Baseline Review</span> goes inside: your systems, policies,
                staff practices, and top-10 risks — with a 30-minute expert readout.
              </p>
              <Link
                to="/baseline-review"
                data-testid="exposure-baseline-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-paper text-sm font-medium px-6 py-3 hover:bg-white/5 transition-colors shrink-0"
              >
                Book the $499 review
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </Reveal>
      )}
    </PageLayout>
  );
}
